"use client";

import { useEffect, useState } from "react";
import { ProjectsList } from "./ProjectsList";
import { ServicesList } from "./ServicesList";
import { ServiceDetail } from "./ServiceDetail";
import { RegulaminGate } from "./RegulaminGate";
import { ProjectTasksPanel } from "./TasksList";
import { navigateToPluginRoute, parsePluginRoute, type PluginRoute } from "./taskRoute";

/**
 * Plugin zarządzania projektami klienta (shell c_user_accounts) - REST/React.
 *
 * Dispatcher zakładek pluginu. Obie akcje są hash-routable i pochodzą
 * z `C_CRM_PROJECT_MANAGEMENT_INC::sharingContentForApi`:
 *   - `projects` (moduł `m_crm_projects`) -> lista projektów usera
 *   - `services` (moduł `m_services`)     -> lista usług z tych projektów
 *
 * **Bramkowanie modułami jest po stronie backendu** - gdy modułu nie ma
 * w instalacji, akcja w ogóle nie trafia do `extension.actions`, więc zakładka
 * nie pojawia się w sidebarze i ten dispatcher nigdy nie dostanie jej actionId.
 * Front celowo NIE duplikuje tej decyzji (jedno źródło prawdy).
 *
 * **Zadania mają własne adresy URL.** Widok wewnątrz zakładki `projects` (lista
 * zadań projektu, formularz zgłoszenia, szczegóły zadania) jest trzymany w hashu
 * jako dodatkowy człon `pm-...` - zob. [taskRoute.ts](./taskRoute.ts). Dzięki temu
 * każdy widok da się skopiować i wysłać dalej, a Wstecz/Dalej w przeglądarce
 * działa bez dodatkowego kodu. Stan lokalny byłby tu ślepą uliczką: po odświeżeniu
 * strony klient wracałby na listę projektów.
 */
export function CCrmProjectManagementView({
  actionId,
  hasWorkLog,
}: {
  actionId: string;
  /**
   * Czy zakładka Dziennik pracy jest dostępna (akcja `worklog` w `extension.actions`,
   * bramkowana modułem po stronie backendu). Decyduje o linku przy projekcie.
   */
  hasWorkLog: boolean;
}) {
  /* Regulamin panelu bramkuje WEJSCIE - dopoki klient go nie zaakceptuje (o ile operator
     tego wymaga), nie pokazujemy ani projektow, ani zadan, ani uslug. */
  return (
    <RegulaminGate>
      <CCrmProjectManagementPanel actionId={actionId} hasWorkLog={hasWorkLog} />
    </RegulaminGate>
  );
}

/** Wlasciwy panel - renderowany dopiero po przejsciu bramki regulaminu. */
function CCrmProjectManagementPanel({
  actionId,
  hasWorkLog,
}: {
  actionId: string;
  hasWorkLog: boolean;
}) {
  const [route, setRoute] = useState<PluginRoute>({ view: "projects" });

  /* Hash czytamy dopiero po zamontowaniu - `location` nie istnieje przy renderze
     serwerowym, a start od `{view:"projects"}` daje zgodny markup po obu stronach. */
  useEffect(() => {
    const sync = () => setRoute(parsePluginRoute(window.location.hash));
    sync();
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);

  /* Zakładka Usługi ma własny drill-down (panel usługi), więc czyta ten sam
     człon `pm-` co zadania. Widoki zadań są ignorowane w tej zakładce i odwrotnie -
     człon jest jeden, ale każda zakładka reaguje tylko na swoje adresy. */
  if (actionId === "services") {
    if (route.view === "service")
      return (
        <ServiceDetail
          key={route.serviceId}
          serviceId={route.serviceId}
          /* Powrót = zdjęcie członu `pm-` z hasha; człon `extension-services`
             zostaje, więc wracamy na listę usług, nie na projekty. */
          onBack={() => navigateToPluginRoute({ view: "projects" })}
        />
      );

    return (
      <ServicesList
        onOpenService={(serviceId) => navigateToPluginRoute({ view: "service", serviceId })}
      />
    );
  }

  /* Dziennik pracy: człon `pm-w{id}` niesie tylko filtr projektu. Zmiana projektu
     w filtrze trafia do hasha, więc zawężony widok da się skopiować i odświeżyć. */
  if (actionId === "worklog")
    return (
      <WorkLog
        projectId={route.view === "worklog" ? route.projectId : null}
        onProjectChange={(projectId) =>
          navigateToPluginRoute(projectId ? { view: "worklog", projectId } : { view: "projects" })
        }
      />
    );

  if (route.view !== "projects" && route.view !== "service" && route.view !== "worklog")
    return (
      <ProjectTasksPanel
        projectId={route.projectId}
        view={route.view}
        taskId={route.view === "detail" ? route.taskId : null}
      />
    );

  return (
    <ProjectsList
      onOpenTasks={(projectId) => navigateToPluginRoute({ view: "tasks", projectId })}
      onAddTask={(projectId) => navigateToPluginRoute({ view: "add", projectId })}
      onOpenWorkLog={hasWorkLog ? navigateToWorkLog : null}
    />
  );
}

/**
 * Czy plugin obsługuje daną akcję (registry w ExtensionActionView).
 * Tylko akcje hash-routable z `sharingContentForApi`.
 */
export function cCrmProjectManagementHandlesAction(actionId: string): boolean {
  return actionId === "projects" || actionId === "services" || actionId === "worklog";
}
